// src/pages/tickets/AdminTicketsPage.tsx

import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Eye,
  Filter,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  ShieldCheck,
  Ticket,
  UserCheck,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

interface TicketUpdate {
  id: string;
  ticket_id: string;
  portal_account_id?: string | null;
  user_id?: string | null;
  update_type: string;
  message: string;
  created_at?: string | null;
}

interface TicketAssignment {
  id: string;
  ticket_id: string;
  assigned_by: string;
  technician_id: string;
  assignment_type: string;
  status: string;
  assignment_note?: string | null;
  assigned_at?: string | null;
  accepted_at?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
  completed_at?: string | null;
  is_current: boolean;
}

interface TicketOut {
  id: string;
  client_id: string;
  portal_account_id: string;
  ticket_number: string;
  subject: string;
  category?: string | null;
  priority: string;
  message: string;
  status: string;
  is_outsourced: boolean;
  resolved_at?: string | null;
  closed_at?: string | null;
  last_updated_at?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  location_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  assignments: TicketAssignment[];
  updates: TicketUpdate[];
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const statusOptions = [
  { value: "all", label: "All Tickets" },
  { value: "open", label: "Open" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];

const normalizeList = <T,>(res: unknown): T[] => {
  if (Array.isArray(res)) return res as T[];

  if (res && typeof res === "object") {
    const obj = res as { items?: T[]; data?: T[]; results?: T[] };
    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.data)) return obj.data;
    if (Array.isArray(obj.results)) return obj.results;
  }

  return [];
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatStatus = (status?: string | null) =>
  String(status || "open")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatPriority = (priority?: string | null) =>
  String(priority || "medium")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusTone = (status?: string | null) => {
  const value = String(status || "").toLowerCase();

  if (value === "closed") {
    return "border-slate-300 bg-slate-200 text-slate-700";
  }

  if (value === "resolved") {
    return "border-emerald-200 bg-emerald-100 text-emerald-800";
  }

  if (value === "assigned" || value === "in_progress") {
    return "border-sky-200 bg-sky-100 text-sky-800";
  }

  if (value === "cancelled") {
    return "border-rose-200 bg-rose-100 text-rose-700";
  }

  return "border-amber-200 bg-amber-100 text-amber-800";
};

const getPriorityTone = (priority?: string | null) => {
  const value = String(priority || "").toLowerCase();

  if (value === "urgent" || value === "high") {
    return "border-rose-200 bg-rose-100 text-rose-700";
  }

  if (value === "medium") {
    return "border-amber-200 bg-amber-100 text-amber-800";
  }

  return "border-emerald-200 bg-emerald-100 text-emerald-800";
};

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${getStatusTone(
      status,
    )}`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {formatStatus(status)}
  </span>
);

const PriorityBadge: React.FC<{ priority?: string | null }> = ({
  priority,
}) => (
  <span
    className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-black ${getPriorityTone(
      priority,
    )}`}
  >
    {formatPriority(priority)}
  </span>
);

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading support tickets…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while customer support tickets are being prepared.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const SupportTicketPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [tickets, setTickets] = useState<TicketOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  const loadTickets = async (manual = false) => {
    try {
      if (manual) setRefreshing(true);
      else setLoading(true);

      setError(null);

      const query =
        status !== "all" ? `?status=${encodeURIComponent(status)}` : "";
      const res = await fetchApp(`/admin/tickets/${query}`);

      setTickets(normalizeList<TicketOut>(res));
    } catch (err: any) {
      const msg = err?.message || "Failed to load support tickets.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTickets(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filteredTickets = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return tickets;

    return tickets.filter((ticket) =>
      [
        ticket.ticket_number,
        ticket.subject,
        ticket.category || "",
        ticket.priority,
        ticket.status,
        ticket.message,
        ticket.location_name || "",
        ticket.portal_account_id,
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [tickets, search]);

  const stats = useMemo(() => {
    const total = filteredTickets.length;
    const open = filteredTickets.filter(
      (item) => String(item.status).toLowerCase() === "open",
    ).length;
    const assigned = filteredTickets.filter((item) =>
      ["assigned", "in_progress"].includes(String(item.status).toLowerCase()),
    ).length;
    const resolved = filteredTickets.filter(
      (item) => String(item.status).toLowerCase() === "resolved",
    ).length;
    const closed = filteredTickets.filter(
      (item) => String(item.status).toLowerCase() === "closed",
    ).length;

    return { total, open, assigned, resolved, closed };
  }, [filteredTickets]);

  const hasFilters = search || status !== "all";

  const clearFilters = () => {
    setSearch("");
    setStatus("all");
  };

  if (loading && tickets.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Admin Support Center
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Support Tickets
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  View, filter, assign, comment, and close customer support
                  tickets from one clean admin workspace.
                </p>
              </div>

              <button
                type="button"
                onClick={() => loadTickets(true)}
                disabled={refreshing}
                className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing support tickets…
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Total"
            value={stats.total}
            sub="Filtered tickets"
            icon={<Ticket className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Open"
            value={stats.open}
            sub="Waiting for action"
            icon={<Clock className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Assigned"
            value={stats.assigned}
            sub="With technician"
            icon={<UserCheck className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Resolved"
            value={stats.resolved}
            sub="Resolved tickets"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Closed"
            value={stats.closed}
            sub="Completed tickets"
            icon={<ShieldCheck className="h-5 w-5" />}
            tone="slate"
          />
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-[14px] font-black text-slate-900">
                Ticket List
              </p>

              <p className="text-[11.5px] font-medium text-slate-500">
                Showing {filteredTickets.length} of {tickets.length} tickets
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center">
              <div className="relative w-full lg:w-96">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search ticket, subject, category..."
                  className={`${inputCls} pl-10`}
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="relative w-full lg:w-64">
                <Filter className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
                >
                  {statusOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>

                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Ticket</th>
                  <th className="px-5 py-3.5 font-black">Subject</th>
                  <th className="px-5 py-3.5 font-black">Category</th>
                  <th className="px-5 py-3.5 font-black">Priority</th>
                  <th className="px-5 py-3.5 font-black">Status</th>
                  <th className="px-5 py-3.5 font-black">Location</th>
                  <th className="px-5 py-3.5 font-black">Created</th>
                  <th className="px-5 py-3.5 text-right font-black">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {filteredTickets.length > 0 ? (
                  filteredTickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="cursor-pointer transition-colors hover:bg-emerald-50/60"
                      onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                            <Ticket className="h-4 w-4" />
                          </div>

                          <div className="min-w-0">
                            <p className="font-mono text-[12.5px] font-black text-slate-900">
                              {ticket.ticket_number}
                            </p>

                            <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                              {ticket.assignments?.length || 0} assignment
                              {(ticket.assignments?.length || 0) === 1
                                ? ""
                                : "s"}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="max-w-[340px] px-5 py-4">
                        <p className="truncate text-[13px] font-black text-slate-900">
                          {ticket.subject}
                        </p>

                        <p className="mt-0.5 line-clamp-1 text-[11.5px] font-medium text-slate-500">
                          {ticket.message}
                        </p>
                      </td>

                      <td className="px-5 py-4 text-[12px] font-bold text-slate-600">
                        {ticket.category || "—"}
                      </td>

                      <td className="px-5 py-4">
                        <PriorityBadge priority={ticket.priority} />
                      </td>

                      <td className="px-5 py-4">
                        <StatusBadge status={ticket.status} />
                      </td>

                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                          <MapPin className="h-3 w-3" />
                          {ticket.location_name || "No location"}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-[12px] font-medium text-slate-500">
                        {formatDateTime(ticket.created_at)}
                      </td>

                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/support/tickets/${ticket.id}`);
                          }}
                          className={`${btnBase} h-9 border border-sky-200 bg-sky-100 px-3 text-sky-800 hover:bg-sky-200`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Ticket className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No tickets found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        No support tickets match your current filters.
                      </p>

                      {hasFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear all filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {filteredTickets.length > 0 ? (
              filteredTickets.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                  className="w-full px-4 py-4 text-left transition-colors hover:bg-emerald-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono text-[12.5px] font-black text-slate-900">
                        {ticket.ticket_number}
                      </p>

                      <p className="mt-1 truncate text-[14px] font-black text-slate-900">
                        {ticket.subject}
                      </p>

                      <p className="mt-1 line-clamp-2 text-[12px] font-medium leading-relaxed text-slate-500">
                        {ticket.message}
                      </p>
                    </div>

                    <StatusBadge status={ticket.status} />
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <PriorityBadge priority={ticket.priority} />

                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                      <MapPin className="h-3 w-3" />
                      {ticket.location_name || "No location"}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Category
                      </p>

                      <p className="mt-1 text-[12px] font-black text-slate-800">
                        {ticket.category || "—"}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                        Created
                      </p>

                      <p className="mt-1 text-[12px] font-black text-slate-800">
                        {formatDateTime(ticket.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 flex justify-end">
                    <span
                      className={`${btnBase} h-9 border border-sky-200 bg-sky-100 px-3 text-sky-800`}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View Details
                    </span>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <Ticket className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No tickets found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  No support tickets match your current filters.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredTickets.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {tickets.length}
              </span>{" "}
              tickets
            </p>

            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketPage;
